import Button from "@/components/Button";
import Switch from "@/components/Switch";
import { useMutation } from "@apollo/client";
import { useState } from "react";
import { ApprovalType, ChangeSetting, changeTypeToDisplayName } from "../utils";
import { SECTION_CHANGEFORM_SET_SETTING_MUTATION, SECTION_CHANGEFORM_SETTINGS_QUERY } from "../queries";
import useAmplitude from "@/hooks/useAmplitude";
import useModal from "@/hooks/useModal";
import { SECTION_ANALYTICS_EVENTS, getSectionAnalyticsData } from "@/pages/sections/_utils/amplitude";
import { showSnackbar, showErrorSnackbar } from "@/lib/snackbarUtils";

export default function EditModal({
  sectionId,
  setting: { id, approvalType, changeType },
  onClosed
}: {
  sectionId: string;
  setting: ChangeSetting;
  onClosed(): void;
}) {
  const { close, Modal } = useModal();
  const { trackEvent } = useAmplitude();

  const [isManual, setIsManual] = useState(ApprovalType.manual === approvalType);

  const [submit, { loading: mutationLoading }] = useMutation(SECTION_CHANGEFORM_SET_SETTING_MUTATION, {
    refetchQueries: [{ query: SECTION_CHANGEFORM_SETTINGS_QUERY, variables: { sectionId } }]
  });

  function flipSwitch() {
    setIsManual((is) => !is);
  }

  async function save() {
    const nextApprovalType = isManual ? ApprovalType.manual : ApprovalType.automatic;

    try {
      const { data } = await submit({
        variables: { input: { changeformSettingId: id, approvalType: nextApprovalType } }
      });

      const { success = false, message = "Something went wrong" } = data?.updateChangeformSetting ?? {};

      if (!success) {
        showErrorSnackbar("Change request setting not updated", { subtitle: message });
        return;
      }

      trackEvent(
        SECTION_ANALYTICS_EVENTS.changeRequestSettingUpdated,
        getSectionAnalyticsData({
          source: "section_change_requests",
          sectionId,
          settingId: id,
          changeType,
          action: "update",
          approvalType: nextApprovalType,
          previousApprovalType: approvalType
        })
      );

      showSnackbar("Change request approval setting updated");
      await close();
    } catch {
      showErrorSnackbar("Change request setting not updated", { subtitle: "Please try again later." });
    }
  }

  const action = (
    <Button icon="check" disabled={mutationLoading} onClick={save}>
      Save
    </Button>
  );

  return (
    <Modal action={action} onClosed={onClosed} headline="Edit Change Request Approver">
      <dl className="flex items-center text-sys-brand-on-surface-variant pb-6">
        <dt className="text-label-large font-semibold">Change Type:</dt>

        <dd className="text-body-medium ml-1">{changeTypeToDisplayName(changeType)}</dd>
      </dl>

      <Switch
        containerClassName="my-2"
        headline="Manual Approval"
        supportingText={
          approvalType === ApprovalType.national
            ? "Currently National approval. Toggle on for Manual, off for Automatic."
            : "When enabled, change requests require manual approval before they are saved."
        }
        selected={isManual}
        onChange={flipSwitch}
      />
    </Modal>
  );
}
